import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDrag, useDrop } from 'react-dnd';
import { updateComponent, selectComponent, updateComponentPosition, selectComponents } from '../redux/editorSlice';
import TextComponent from './TextComponent';
import ImageComponent from './ImageComponent';
import ContainerComponent from './ContainerComponent';
import ButtonComponent from './ButtonComponent';
import LoginForm from './LoginComponent';
import BoardComponent from './BoardComponent';
import ProductDetailComponent from './ProductDetailComponent';
import ProductListComponent from './ProductListComponent';
import ProductSliderComponent from './ProductSliderComponent';
import SignUpComponent from './SignUpComponent';
import MainBannerComponent from './MainBannerComponent';
import RowComponent from './RowComponent';
import ColumnComponent from './ColumnComponent';
import CartComponent from './CartComponent';
import PaymentComponent from './PaymentComponent';
import PaymentSuccess from './PaymentSuccess';
import PaymentFail from './PaymentFail';
import FAQComponent from './FAQComponent';
import OrderHistoryComponent from './OrderHistoryComponent';
import CompanyHistoryComponent from './CompanyHistoryComponent';
import AssetHistoryComponent from './AssetHistoryComponent';
import MembershipSignupComponent from './MembershipSignupComponent';
import BinaryLegsComponent from './BinaryLegsComponent';
import SmartEditor2Component from './SmartEditor2Component';
import UserProfileComponent from './UserProfileComponent';
import ImageTextComponent from './ImageTextComponent';
import TrendingItemsComponent from './TrendingItemsComponent';
import FixedBgTextComponent from './FixedBgTextComponent';
import ReviewFormComponent from './ReviewFormComponent';
import TabLayoutComponent from './TabLayoutComponent';
import RecommendedUsersComponent from './RecommendedUsersComponent';
import CustomMembershipSignupComponent from './CustomMembershipSignupComponent';
import { COMPONENT_TYPES } from '../constants';
import TempOne from './TempOne';
import TempTwo from './TempTwo';
// import './ComponentRenderer.css';

// 디바이스 뷰 모드 컨텍스트
export const DeviceViewContext = React.createContext('pc');

function ComponentRenderer({ component, components, isSelected, onSelect, viewMode = 'pc' }) {
  const dispatch = useDispatch();
  const storeComponents = useSelector(selectComponents);
  const allComponents = components || storeComponents;
  const [resizing, setResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [startSize, setStartSize] = useState({ width: 0, height: 0 });
  const componentRef = useRef(null);
  // 게시판 컴포넌트 강제 업데이트를 위한 상태 추가
  const [forceUpdateCounter, setForceUpdateCounter] = useState(0);
  
  // 이 컴포넌트의 자식 컴포넌트들 찾기
  const childComponents = allComponents.filter(comp => comp.parentId === component.id);
  
  // 부모 컴포넌트 찾기
  const parentComponent = component.parentId ? allComponents.find(comp => comp.id === component.parentId) : null;
  const _isChildOfColumn = parentComponent?.type === COMPONENT_TYPES.COLUMN;
  const _isChildOfRow = parentComponent?.type === COMPONENT_TYPES.ROW;
  
 
  
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'COMPONENT',
    item: { 
      id: component.id, 
      type: component.type,
      componentType: component.type,
      parentId: component.parentId,
      order: component.order,
      hasChildren: childComponents.length > 0
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
    end: (item, monitor) => {
      if (!monitor.didDrop()) {
        dispatch(updateComponentPosition({ 
          id: component.id, 
          newPosition: component.position,
          parentId: component.parentId
        }));
      }
    }
  }), [component.id, component.type, component.parentId, component.order, childComponents.length, component.position]);

  const [, drop] = useDrop(() => ({
    accept: 'COMPONENT',
    drop: (item, monitor) => {
      if (component.type === COMPONENT_TYPES.ROW || component.type === COMPONENT_TYPES.COLUMN) {
        return;
      }
      
      const offset = monitor.getClientOffset();
      if (!offset) return;

      const canvas = document.getElementById('editor-canvas');
      if (!canvas) return;

      const canvasRect = canvas.getBoundingClientRect();
      let x = offset.x - canvasRect.left;
      let y = offset.y - canvasRect.top;

      if (item.id === component.id) {
        dispatch(updateComponentPosition({ 
          id: component.id, 
          newPosition: { x, y },
          parentId: null
        }));
      }
    },
  }), [component.id, component.type]);
  
  const handleSelect = useCallback((e) => {
    e.stopPropagation();
    
    // 만약 외부에서 onSelect prop이 전달되었다면 그것을 사용
    if (onSelect) {
      onSelect();
    } else {
      // 기존에 사용하던 dispatch 로직
      dispatch(selectComponent(component.id));
    }
  }, [component.id, dispatch, onSelect]);
  
  // 부모 컴포넌트의 너비와 높이 구하기
  const getParentDimensions = useCallback(() => {
    if (!component.parentId) return null;
    
    const parentEl = document.getElementById(
      parentComponent?.type === COMPONENT_TYPES.ROW 
        ? `row-${component.parentId}`
        : parentComponent?.type === COMPONENT_TYPES.COLUMN
          ? `col-${component.parentId}`
          : null
    );
    
    if (!parentEl) return null;
    
    const rect = parentEl.getBoundingClientRect();
    return { width: rect.width, height: rect.height };
  }, [component.parentId, parentComponent]);
  
  // 리사이즈 중 핸들러를 useCallback으로 감싸 의존성 문제 해결
  const handleResizeMove = useCallback((e) => {
    if (!resizing) return;
    
    const deltaX = e.clientX - startPos.x;
    const deltaY = e.clientY - startPos.y;
    let newWidth = '100%'; // 항상 width를 100%로 설정
    let newHeight = startSize.height;
    let newX = 0; // x 좌표는 항상 0
    let newY = component.position?.y || 0;
    
    // 방향에 따른 크기 및 위치 조정
    switch (resizeDirection) {
      case 'e':
        // width는 100%로 고정, paddingRight 조정
        dispatch(updateComponent({
          id: component.id,
          changes: {
            style: {
              ...(component.style || {}),
              paddingRight: Math.max(0, parseInt((component.style?.paddingRight || '0')) - Math.floor(deltaX)) + 'px'
            }
          }
        }));
        return;
      case 'w':
        // width는 100%로 고정, paddingLeft 조정
        dispatch(updateComponent({
          id: component.id,
          changes: {
            style: {
              ...(component.style || {}),
              paddingLeft: Math.max(0, parseInt((component.style?.paddingLeft || '0')) + Math.floor(deltaX)) + 'px'
            }
          }
        }));
        return;
      case 's':
        newHeight = startSize.height + deltaY;
        break;
      case 'n':
        newHeight = startSize.height - deltaY;
        newY = (component.position?.y || 0) + deltaY;
        break;
      case 'se':
        // width는 100%로 고정, 높이만 조정
        newHeight = startSize.height + deltaY;
        dispatch(updateComponent({
          id: component.id,
          changes: {
            style: {
              ...(component.style || {}),
              paddingRight: Math.max(0, parseInt((component.style?.paddingRight || '0')) - Math.floor(deltaX)) + 'px'
            }
          }
        }));
        break;
      case 'sw':
        // width는 100%로 고정, 높이만 조정
        newHeight = startSize.height + deltaY;
        dispatch(updateComponent({
          id: component.id,
          changes: {
            style: {
              ...(component.style || {}),
              paddingLeft: Math.max(0, parseInt((component.style?.paddingLeft || '0')) + Math.floor(deltaX)) + 'px'
            }
          }
        }));
        break;
      case 'ne':
        // width는 100%로 고정, 높이만 조정
        newHeight = startSize.height - deltaY;
        newY = (component.position?.y || 0) + deltaY;
        dispatch(updateComponent({
          id: component.id,
          changes: {
            style: {
              ...(component.style || {}),
              paddingRight: Math.max(0, parseInt((component.style?.paddingRight || '0')) - Math.floor(deltaX)) + 'px'
            }
          }
        }));
        break;
      case 'nw':
        // width는 100%로 고정, 높이만 조정
        newHeight = startSize.height - deltaY;
        newY = (component.position?.y || 0) + deltaY;
        dispatch(updateComponent({
          id: component.id,
          changes: {
            style: {
              ...(component.style || {}),
              paddingLeft: Math.max(0, parseInt((component.style?.paddingLeft || '0')) + Math.floor(deltaX)) + 'px'
            }
          }
        }));
        break;
      default:
        break;
    }
    
    // 최소 크기 제한
    newHeight = Math.max(20, newHeight);
    
    // 부모 컴포넌트 크기 제한 적용
    if (component.parentId) {
      const parentDimensions = getParentDimensions();
      if (parentDimensions) {
        // 부모 컴포넌트의 크기와 현재 컴포넌트의 위치를 고려하여 최대 크기 계산
        const maxHeight = parentDimensions.height - newY;
        
        // 부모 컴포넌트 영역을 넘지 않도록 제한
        if (newHeight > maxHeight) {
          newHeight = maxHeight;
          // 위로 리사이즈할 때 위치 조정
          if (resizeDirection.includes('n')) {
            newY = (component.position?.y || 0) + (startSize.height - newHeight);
          }
        }
      }
    }
    
    // 높이만 업데이트
    if (resizeDirection.includes('n') || resizeDirection.includes('s')) {
      dispatch(updateComponent({
        id: component.id,
        changes: {
          size: { width: '100%', height: newHeight },
          position: { x: 0, y: newY }
        }
      }));
    }
  }, [resizing, resizeDirection, startPos, startSize, component, dispatch, getParentDimensions]);
  
  // 리사이즈 종료 핸들러도 useCallback으로 감싸기
  const handleResizeEnd = useCallback(() => {
    setResizing(false);
    setResizeDirection(null);
    
    // 전역 이벤트 리스너 제거
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  }, [handleResizeMove]);
  
  // 리사이즈 시작 핸들러
  const handleResizeStart = useCallback((e, direction) => {
    e.stopPropagation();
    e.preventDefault();
    
    handleSelect(e); // 컴포넌트 선택
    setResizing(true);
    setResizeDirection(direction);
    setStartPos({ x: e.clientX, y: e.clientY });
    
    // 컴포넌트 사이즈에 안전하게 접근
    const defaultSize = { width: '100%', height: 'auto' };
    const size = component.size || defaultSize;
    
    setStartSize({ 
      width: size.width || defaultSize.width, 
      height: size.height || defaultSize.height 
    });
    
    // 전역 이벤트 리스너 등록
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  }, [component.size, handleResizeMove, handleResizeEnd, handleSelect]);
  
  // 컴포넌트 언마운트 시 이벤트 리스너 정리
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [handleResizeMove, handleResizeEnd]);
  
  // BoardComponent의 스타일이 변경될 때 강제 재렌더링을 위한 효과
  useEffect(() => {
    if (component.type === 'BOARD' || component.type === COMPONENT_TYPES.BOARD) {
      // 스타일이나 데이터가 변경될 때마다 forceUpdateCounter 증가
      setForceUpdateCounter(prev => prev + 1);
    }
  }, [component.type, component.style, component.data]);
  
  // 컴포넌트 스타일 계산
  const getComponentStyles = () => {
    const isInMobile = viewMode === 'mobile';
    const isInTablet = viewMode === 'tablet';
    
    // 디바이스 타입 결정 (태블릿과 모바일은 mobile로 통합)
    const deviceType = isInMobile || isInTablet ? 'mobile' : 'pc';
    
    // 기본 스타일
    let baseStyles = {
      position: 'relative',
      width: component.size?.width || '100%',
      height: component.size?.height || 'auto',
      boxSizing: 'border-box',
      overflow: 'visible',
      // 컴포넌트 스타일 병합
      ...(component.style || {})
    };
    
    // 디바이스별 속성 적용 - PC일 때는 PC 설정만 적용, 모바일/태블릿일 때는 mobile 설정만 적용
    if (component.style?.deviceProperty) {
      if (deviceType === 'pc' && component.style.deviceProperty['pc']) {
        // PC 모드일 때는 PC 설정만 적용
        baseStyles = {
          ...baseStyles,
          ...component.style.deviceProperty['pc']
        };
      } else if (deviceType === 'mobile' && component.style.deviceProperty['mobile']) {
        // 모바일/태블릿 모드일 때는 모바일 설정만 적용
        baseStyles = {
          ...baseStyles,
          ...component.style.deviceProperty['mobile']
        };
      }
    }
    
    // 모바일/태블릿 모드에서의 스타일 조정
    if (isInMobile || isInTablet) {
      baseStyles = {
        ...baseStyles,
        width: '100%',
        maxWidth: '100%',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        hyphens: 'auto',
        textOverflow: 'ellipsis',
        overflow: 'visible' // 내용이 짤리지 않도록 visible로 설정
      };
      
      // 텍스트 관련 컴포넌트 처리
      if (component.type === 'TEXT') {
        baseStyles.fontSize = isInMobile ? 
          `${Math.max(12, parseInt(baseStyles.fontSize || '16px') * 0.85)}px` : 
          baseStyles.fontSize;
        baseStyles.lineHeight = '1.4';
        baseStyles.whiteSpace = 'normal';
      }
      
      // 이미지 관련 컴포넌트 처리
      if (component.type === 'IMAGE') {
        baseStyles.maxWidth = '100%';
        baseStyles.height = 'auto';
        baseStyles.objectFit = 'contain';
      }
      
      // 컨테이너 관련 컴포넌트 처리
      if (component.type === 'CONTAINER' || component.type === 'ROW' || component.type === 'COLUMN') {
        baseStyles.padding = '5px';
        baseStyles.overflow = 'visible';
      }
    }
    
    // 선택된 컴포넌트 스타일
    if (isSelected) {
      baseStyles.outline = '2px solid #3b82f6';
      baseStyles.zIndex = 1;
    }
    
    return baseStyles;
  };
  
  // 컴포넌트 타입에 따라 실제 컴포넌트 렌더링 
  const renderComponent = (componentType, props) => {
    // 디바이스 타입 결정 (태블릿과 모바일은 mobile로 통합)
    const deviceType = viewMode === 'mobile' || viewMode === 'tablet' ? 'mobile' : 'pc';
    
    // 디바이스별 componentProps 적용
    let deviceProps = {...props};
    
    // 디바이스별 스타일 적용 - PC일 때는 PC 설정만 적용, 모바일/태블릿일 때는 mobile 설정만 적용
    if (component.style?.deviceProperty) {
      if (deviceType === 'pc' && component.style.deviceProperty['pc']) {
        // PC 모드일 때는 PC 설정만 적용
        deviceProps.style = {
          ...deviceProps.style,
          ...component.style.deviceProperty['pc']
        };
      } else if (deviceType === 'mobile' && component.style.deviceProperty['mobile']) {
        // 모바일/태블릿 모드일 때는 모바일 설정만 적용
        deviceProps.style = {
          ...deviceProps.style,
          ...component.style.deviceProperty['mobile']
        };
      }
    }
    
    // 디바이스별 componentProps 적용 - 같은 방식으로 PC/모바일 분리 적용
    if (component.componentProps?.deviceProperty) {
      if (deviceType === 'pc' && component.componentProps.deviceProperty['pc']) {
        // PC 모드일 때는 PC 설정만 적용
        deviceProps.componentProps = {
          ...deviceProps.componentProps,
          ...component.componentProps.deviceProperty['pc']
        };
      } else if (deviceType === 'mobile' && component.componentProps.deviceProperty['mobile']) {
        // 모바일/태블릿 모드일 때는 모바일 설정만 적용
        deviceProps.componentProps = {
          ...deviceProps.componentProps,
          ...component.componentProps.deviceProperty['mobile']
        };
      }
    }
    
    // 디바이스별 data 적용 - 같은 방식으로 PC/모바일 분리 적용
    if (component.data?.deviceProperty) {
      if (deviceType === 'pc' && component.data.deviceProperty['pc']) {
        // PC 모드일 때는 PC 설정만 적용
        deviceProps.data = {
          ...deviceProps.data,
          ...component.data.deviceProperty['pc']
        };
      } else if (deviceType === 'mobile' && component.data.deviceProperty['mobile']) {
        // 모바일/태블릿 모드일 때는 모바일 설정만 적용
        deviceProps.data = {
          ...deviceProps.data,
          ...component.data.deviceProperty['mobile']
        };
      }
    }
    
    // 디바이스별 content 적용 - 같은 방식으로 PC/모바일 분리 적용
    if (component.content?.deviceProperty) {
      if (deviceType === 'pc' && component.content.deviceProperty['pc']) {
        // PC 모드일 때는 PC 설정만 적용
        if (typeof component.content === 'object') {
          deviceProps.content = {
            ...deviceProps.content,
            ...component.content.deviceProperty['pc']
          };
        } else if (typeof component.content.deviceProperty['pc'] !== 'undefined') {
          deviceProps.content = component.content.deviceProperty['pc'];
        }
      } else if (deviceType === 'mobile' && component.content.deviceProperty['mobile']) {
        // 모바일/태블릿 모드일 때는 모바일 설정만 적용
        if (typeof component.content === 'object') {
          deviceProps.content = {
            ...deviceProps.content,
            ...component.content.deviceProperty['mobile']
          };
        } else if (typeof component.content.deviceProperty['mobile'] !== 'undefined') {
          deviceProps.content = component.content.deviceProperty['mobile'];
        }
      }
    }
    
    // 타입 체크를 위한 문자열 변환 및 표준화
    const typeStr = typeof componentType === 'string' ? componentType.toUpperCase() : String(componentType).toUpperCase();
    
    // USER_PROFILE 타입 체크
    if (typeStr === 'USER_PROFILE' || componentType === COMPONENT_TYPES.USER_PROFILE) {
      return <UserProfileComponent {...deviceProps} />;
    }
    
    // RECOMMENDED_USERS 타입 체크
    if (typeStr === 'RECOMMENDED_USERS' || componentType === COMPONENT_TYPES.RECOMMENDED_USERS) {
      return <RecommendedUsersComponent {...deviceProps} />;
    }
    
    // 렌더링 분기 추가
    if (component.type === 'TEMP_ONE') {
      return <TempOne {...component} />;
    }
    
    switch (componentType) {
      case 'TEXT':
        return <TextComponent 
          content={deviceProps.content} 
          style={{
            ...deviceProps.style,
            ...(viewMode === 'mobile' ? {
              fontSize: `${Math.max(12, parseInt(deviceProps.style?.fontSize || '16px') * 0.85)}px`,
              lineHeight: '1.4',
              maxWidth: '100%',
              width: '100%',
              overflowWrap: 'break-word',
              wordWrap: 'break-word',
              boxSizing: 'border-box',
              whiteSpace: 'normal',
              overflow: 'visible'
            } : {})
          }} 
          className={`responsive-text device-${viewMode}`} 
        />;
      case 'IMAGE':
        return <ImageComponent 
          src={deviceProps.content?.src || ''} 
          alt={deviceProps.content?.alt || ''} 
          content={deviceProps.content || { src: '', alt: '' }}
          style={{
            ...deviceProps.style,
            maxWidth: '100%',
            ...(viewMode === 'mobile' ? {
              height: 'auto',
              objectFit: 'contain'
            } : {})
          }}
          className={`responsive-image device-${viewMode}`}
        />;
      case 'CONTAINER':
        return (
          <ContainerComponent 
            style={{
              ...deviceProps.style,
              ...(viewMode === 'mobile' ? {
                maxWidth: '100%',
                width: '100%',
                padding: '5px',
                boxSizing: 'border-box',
                overflow: 'visible'
              } : {})
            }} 
            className={`responsive-container device-${viewMode}`}
          >
            {childComponents.length > 0 ? 
              childComponents.map(child => (
                <ComponentRenderer 
                  key={child.id} 
                  component={child} 
                  viewMode={viewMode}
                />
              )) 
              : deviceProps.content
            }
          </ContainerComponent>
        );
      case 'BUTTON':
        return <ButtonComponent content={deviceProps.content || '버튼'} style={deviceProps.style} className={`responsive-button device-${viewMode}`} />;
      case 'LOGIN':
        return <LoginForm style={deviceProps.style} viewMode={viewMode} />;
      case 'BOARD':
        
        
        return <BoardComponent 
          key={`board-${deviceProps.id}-${forceUpdateCounter || 0}`}
          style={{
            ...deviceProps.style,
            paddingTop: deviceProps.style?.device?.[deviceType]?.paddingTop || deviceProps.style?.paddingTop || '0px',
            paddingRight: deviceProps.style?.device?.[deviceType]?.paddingRight || deviceProps.style?.paddingRight || '0px',
            paddingBottom: deviceProps.style?.device?.[deviceType]?.paddingBottom || deviceProps.style?.paddingBottom || '0px',
            paddingLeft: deviceProps.style?.device?.[deviceType]?.paddingLeft || deviceProps.style?.paddingLeft || '0px',
          }} 
          data={deviceProps.data} 
          viewMode={viewMode} 
        />;
      case 'PRODUCT_DETAIL':
        return <ProductDetailComponent 
          style={{...deviceProps.style, width: "100%", maxWidth: "100%"}} 
          componentProps={{ 
            order: "", 
            none: "none", 
            lg: 12,
            showDescriptionTab: deviceProps.showDescriptionTab !== false,
            showReviewsTab: deviceProps.showReviewsTab !== false,
            showSpecificationTab: deviceProps.showSpecificationTab !== false
          }}
          viewMode={viewMode}
        />;
      case 'PRODUCT_LIST':
        return (
          <ProductListComponent 
            key={`product-list-${deviceProps.id}-${deviceProps.itemsPerRow || 4}`}
            componentProps={{
              ...deviceProps,
              itemsPerRow: viewMode === 'mobile' ? 1 : (viewMode === 'tablet' ? 2 : 4)
            }}
            style={{
              ...deviceProps.style,
              ...(viewMode === 'mobile' || viewMode === 'tablet' ? {
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                overflow: 'visible',
                padding: '5px'
              } : {})
            }}
            viewMode={viewMode}
          />
        );
      case 'PRODUCT_SLIDER':
        return (
          <ProductSliderComponent 
            key={`product-slider-${deviceProps.id}-${deviceProps.itemsPerSlide || 4}`}
            componentProps={{
              ...deviceProps,
              itemsPerSlide: viewMode === 'mobile' ? 1 : (viewMode === 'tablet' ? 2 : (deviceProps.itemsPerSlide || 4)),
              autoSlide: deviceProps.autoSlide !== false,
              autoSlideInterval: deviceProps.autoSlideInterval || 2000,
              imageHeight: deviceProps.imageHeight || 250
            }}
            style={{
              ...deviceProps.style,
              width: '100%',
              ...(viewMode === 'mobile' || viewMode === 'tablet' ? {
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                overflow: 'visible',
                padding: '0 5px'
              } : {})
            }}
            viewMode={viewMode}
          />
        );
      case 'SIGNUP':
        return <SignUpComponent 
          style={{
            ...deviceProps.style,
            ...(viewMode === 'mobile' || viewMode === 'tablet' ? {
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflow: 'visible',
              padding: '5px'
            } : {})
          }} 
          viewMode={viewMode} 
        />;
      case 'MAIN_BANNER':
        return <MainBannerComponent 
          componentProps={{
            ...deviceProps,
            // 모바일에서는 단일 슬라이드 표시
            slidesToShow: viewMode === 'mobile' ? 1 : (deviceProps.slidesToShow || 1)
          }}
          mode={deviceProps.mode || 'slider'} 
          style={{
            ...deviceProps.style,
            ...(viewMode === 'mobile' || viewMode === 'tablet' ? {
              width: '100%',
              maxWidth: '100%',
              height: 'auto',
              minHeight: viewMode === 'mobile' ? '200px' : '300px',
              boxSizing: 'border-box',
              overflow: 'hidden'
            } : {})
          }}
          viewMode={viewMode}
        />;
      case 'CART':
        return <CartComponent 
          componentProps={{...deviceProps.data, ...deviceProps}} 
          style={{
            ...deviceProps.style,
            ...(viewMode === 'mobile' || viewMode === 'tablet' ? {
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflow: 'visible',
              padding: '5px'
            } : {})
          }}
          viewMode={viewMode}
        />;
      case 'PAYMENT':
        return <PaymentComponent 
          componentProps={{...deviceProps.data, ...deviceProps}} 
          style={{
            ...deviceProps.style,
            ...(viewMode === 'mobile' || viewMode === 'tablet' ? {
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflow: 'visible',
              padding: '5px'
            } : {})
          }}
          viewMode={viewMode}
        />;
      case 'PAYMENT_SUCCESS':
        return <PaymentSuccess 
          componentProps={{...deviceProps.data, ...deviceProps}} 
          style={{
            ...deviceProps.style,
            ...(viewMode === 'mobile' || viewMode === 'tablet' ? {
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflow: 'visible',
              padding: '5px'
            } : {})
          }}
          viewMode={viewMode}
        />;
      case 'PAYMENT_FAIL':
        return <PaymentFail 
          componentProps={{...deviceProps.data, ...deviceProps}} 
          style={{
            ...deviceProps.style,
            ...(viewMode === 'mobile' || viewMode === 'tablet' ? {
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflow: 'visible',
              padding: '5px'
            } : {})
          }}
          viewMode={viewMode}
        />;
      case 'FAQ':
        return <FAQComponent 
          id={deviceProps.id} 
          faqItems={deviceProps.faqItems || []} 
          style={{
            ...deviceProps.style,
            ...(viewMode === 'mobile' || viewMode === 'tablet' ? {
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflow: 'visible',
              padding: '5px'
            } : {})
          }}
          viewMode={viewMode}
        />;
      case 'ORDER_HISTORY':
        return <OrderHistoryComponent 
          componentProps={{
            className: "order-history-wrapper padding-tb-40",
            title: deviceProps.title || "주문 내역",
            emptyText: deviceProps.emptyText || "주문 내역이 없습니다."
          }} 
          style={{
            ...deviceProps.style,
            ...(viewMode === 'mobile' || viewMode === 'tablet' ? {
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflow: 'visible',
              padding: '5px'
            } : {})
          }}
          viewMode={viewMode}
        />;
      case 'MILEAGE_HISTORY':
      case COMPONENT_TYPES.MILEAGE_HISTORY:
      case COMPONENT_TYPES.ASSET_HISTORY:
        return <AssetHistoryComponent 
          componentProps={{
            className: "order-history-wrapper padding-tb-40",
            title: "자산 내역",
            emptyText: "자산 내역이 없습니다."
          }}
          id={deviceProps.id} 
          style={{
            ...deviceProps.style,
            ...(viewMode === 'mobile' || viewMode === 'tablet' ? {
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflow: 'visible',
              padding: '5px'
            } : {})
          }}
          isPreview={false}
        />;
      case 'COMPANY_HISTORY':
      case COMPONENT_TYPES.COMPANY_HISTORY:
        return <CompanyHistoryComponent 
          id={deviceProps.id}
          title={deviceProps.title || "회사 연혁"}
          subtitle={deviceProps.subtitle || "저희 회사의 성장 과정을 소개합니다"}
          timelineStyle={viewMode === 'mobile' ? 'vertical' : (deviceProps.timelineStyle || "vertical")}
          colorScheme={deviceProps.colorScheme || {
            primary: '#3498db',
            secondary: '#f1c40f',
            background: '#ffffff',
            text: '#333333'
          }}
          animation={deviceProps.animation !== undefined ? deviceProps.animation : true}
          historyItems={deviceProps.historyItems}
          imageWidth={viewMode === 'mobile' ? 100 : (deviceProps.imageWidth || deviceProps.imageSize || 150)}
          imageHeight={viewMode === 'mobile' ? 100 : (deviceProps.imageHeight || deviceProps.imageSize || 150)}
          style={{
            ...deviceProps.style,
            ...(viewMode === 'mobile' || viewMode === 'tablet' ? {
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflow: 'visible',
              padding: '5px',
              fontSize: viewMode === 'mobile' ? '14px' : 'inherit'
            } : {})
          }}
          viewMode={viewMode}
        />;
      case 'MEMBERSHIP_SIGNUP':
        return <MembershipSignupComponent 
          id={deviceProps.id}
          componentProps={{
            className: deviceProps.className || "membership-signup-wrapper",
            title: deviceProps.title || "멤버십 가입신청",
            productId: Number(deviceProps.productId) || 0,
            buttonText: deviceProps.buttonText || "가입 신청",
            completedText: deviceProps.completedText || "가입 완료",
            description: deviceProps.description || "멤버십에 가입하시면 다양한 혜택을 받으실 수 있습니다."
          }} 
          style={deviceProps.style} 
          isPreview={false} 
        />;
      case 'BINARY_LEGS':
        return <BinaryLegsComponent 
          id={deviceProps.id}
          componentProps={{
            title: deviceProps.title || "후원 레그"
          }} 
          style={deviceProps.style} 
        />;
      case 'RICH_EDITOR':
        return <SmartEditor2Component id={deviceProps.id} componentProps={deviceProps.componentProps} style={deviceProps.style} className={deviceProps.className} />;
      case 'IMAGE_TEXT':
        return <ImageTextComponent 
          id={deviceProps.id}
          style={deviceProps.style}
          componentProps={deviceProps.componentProps || {}}
          content={deviceProps.content}
          isPreview={false}
          viewMode={viewMode}
        />;
      case 'TRENDING_ITEMS':
        return <TrendingItemsComponent 
          id={deviceProps.id}
          style={deviceProps.style}
          componentProps={deviceProps.componentProps || {}}
          isPreview={false}
          viewMode={viewMode}
        />;
      case 'FIXED_BG_TEXT':
        return <FixedBgTextComponent {...deviceProps} viewMode={viewMode} />;
      case COMPONENT_TYPES.REVIEW_FORM:
        return <ReviewFormComponent {...deviceProps} isPreview={false} viewMode={viewMode} />;
      case 'TAB_LAYOUT':
        return <TabLayoutComponent {...deviceProps} />;
      case 'RECOMMENDED_USERS':
      case COMPONENT_TYPES.RECOMMENDED_USERS:
        return <RecommendedUsersComponent {...deviceProps} />;
      case COMPONENT_TYPES.CUSTOM_MEMBERSHIP_SIGNUP:
      case 'CUSTOM_MEMBERSHIP_SIGNUP':
        return (
          <CustomMembershipSignupComponent
            id={deviceProps.id}
            productId={Number(deviceProps.productId) || 0}
            title={deviceProps.title || "자유 멤버십 가입"}
            description={deviceProps.description || "직접 금액을 입력해 가입/추가 결제할 수 있습니다."}
            buttonText={deviceProps.buttonText || "가입/추가 결제"}
            completedText={deviceProps.completedText || "추가 결제가 완료되었습니다."}
            showRefundVisualization={deviceProps.showRefundVisualization !== false}
            refundLimitPercent={Number(deviceProps.refundLimitPercent) || 400}
            assetOptions={deviceProps.assetOptions || []}
            minAmount={Number(deviceProps.minAmount) || 10000}
            style={deviceProps.style}
            isPreview={true}
          />
        );
      default:
        console.warn('알 수 없는 컴포넌트 타입:', componentType);
        return <div>Unknown component type</div>;
    }
  };
  
  // 그리드 컴포넌트 여부 확인 (Row 또는 Column)
  const isGridComponent = component.type === COMPONENT_TYPES.ROW || component.type === COMPONENT_TYPES.COLUMN;
  
  // 부모가 그리드 컴포넌트인지 확인
  const isChildOfGridComponent = parentComponent && (parentComponent.type === COMPONENT_TYPES.ROW || parentComponent.type === COMPONENT_TYPES.COLUMN);
  
  // 그리드 내부 자식 컴포넌트의 스타일 조정
  let componentStyle;
  
  // 컴포넌트에 size 속성이 없을 경우 기본값 제공
  const componentSize = component.size || { width: '100%', height: 'auto' };
  
  // 자동 높이 사용 여부 확인 (ProductDetailComponent, ProductListComponent 같은 컴포넌트는 자동 높이 사용)
  const useAutoHeight = component.type === COMPONENT_TYPES.PRODUCT_DETAIL || 
                        component.type === COMPONENT_TYPES.PRODUCT_LIST ||
                        component.type === COMPONENT_TYPES.SIGNUP ||
                        component.type === COMPONENT_TYPES.MAIN_BANNER ||
                        component.type === COMPONENT_TYPES.CART ||
                        component.type === COMPONENT_TYPES.PAYMENT ||
                        component.type === COMPONENT_TYPES.PAYMENT_SUCCESS ||
                        component.type === COMPONENT_TYPES.PAYMENT_FAIL ||
                        component.type === COMPONENT_TYPES.FAQ ||
                        component.type === COMPONENT_TYPES.ORDER_HISTORY ||
                        component.type === COMPONENT_TYPES.COMPANY_HISTORY ||
                        component.type === COMPONENT_TYPES.MILEAGE_HISTORY ||
                        component.type === COMPONENT_TYPES.CUSTOM_MEMBERSHIP_SIGNUP ||
                        component.type === COMPONENT_TYPES.MEMBERSHIP_SIGNUP ||
                        component.type === COMPONENT_TYPES.BINARY_LEGS ||
                        component.type === COMPONENT_TYPES.RICH_EDITOR ||
                        component.type === COMPONENT_TYPES.PRODUCT_SLIDER ||
                        component.type === COMPONENT_TYPES.USER_PROFILE ||
                        component.type === COMPONENT_TYPES.IMAGE_TEXT ||
                        component.type === COMPONENT_TYPES.TRENDING_ITEMS ||
                        component.type === COMPONENT_TYPES.FIXED_BG_TEXT ||
                        component.type === COMPONENT_TYPES.BOARD ||
                        component.type === COMPONENT_TYPES.REVIEW_FORM ||
                        component.type === COMPONENT_TYPES.TAB_LAYOUT ||
                        component.type === COMPONENT_TYPES.RECOMMENDED_USERS ||
                        component.type === COMPONENT_TYPES.TEMP_ONE ||
                        component.type === 'PRODUCT_DETAIL' ||
                        component.type === 'TEMP_ONE' ||
                        component.type === 'PRODUCT_LIST' ||
                        component.type === 'SIGNUP' ||
                        component.type === 'CUSTOM_MEMBERSHIP_SIGNUP' ||
                        component.type === 'MAIN_BANNER' ||
                        component.type === 'CART' ||
                        component.type === 'PAYMENT' ||
                        component.type === 'PAYMENT_SUCCESS' ||
                        component.type === 'PAYMENT_FAIL' ||
                        component.type === 'FAQ' ||
                        component.type === 'ORDER_HISTORY' ||
                        component.type === 'COMPANY_HISTORY' ||
                        component.type === 'MILEAGE_HISTORY' ||
                        component.type === 'MEMBERSHIP_SIGNUP' ||
                        component.type === 'BINARY_LEGS' ||
                        component.type === 'PRODUCT_SLIDER' ||
                        component.type === 'IMAGE_TEXT' ||
                        component.type === 'TRENDING_ITEMS' ||
                        component.type === 'FIXED_BG_TEXT' ||
                        component.type === 'BOARD' ||
                        component.type === 'REVIEW_FORM' ||
                        component.type === 'TAB_LAYOUT' ||
                        component.type === 'RICH_EDITOR';

  if (isGridComponent && isChildOfGridComponent) {
    // 다른 그리드 컴포넌트 내부에 있는 Row, Column
    componentStyle = {
      cursor: resizing ? 'auto' : 'move',
      opacity: isDragging ? 0.5 : 1,
      border: component.isSelected ? '2px solid #007bff' : 'none',
      boxSizing: 'border-box',
      position: 'relative', // absolute에서 relative로 변경
      width: '100%', // 항상 100% 너비 보장
      height: useAutoHeight ? 'auto' : `${componentSize.height}px`,
      minHeight: useAutoHeight ? '200px' : undefined, // 자동 높이일 경우 최소 높이 지정
      zIndex: component.isSelected ? 10 : 1,
      paddingLeft: '0px',
      paddingRight: '0px',
      paddingTop: component.style?.paddingTop || '0px',
      paddingBottom: component.style?.paddingBottom || '0px',
      ...(component.style || {})
    };
  } else if (isGridComponent) {
    // 최상위 그리드 컴포넌트
    componentStyle = {
      cursor: 'default',
      opacity: isDragging ? 0.5 : 1,
      border: component.isSelected ? '2px solid #007bff' : 'none',
      boxSizing: 'border-box',
      position: 'relative', // absolute에서 relative로 변경
      width: '100%', // 항상 100% 너비 보장
      height: useAutoHeight ? 'auto' : `${componentSize.height}px`,
      minHeight: useAutoHeight ? '200px' : undefined, // 자동 높이일 경우 최소 높이 지정
      zIndex: component.isSelected ? 10 : 1,
      backgroundColor: 'transparent',
      borderRadius: '0px',
      paddingLeft: '0px',
      paddingRight: '0px',
      paddingTop: '0px',
      paddingBottom: '0px',
      ...(component.style || {})
    };
  } else if (isChildOfGridComponent) {
    // 그리드 내부의 일반 컴포넌트
    componentStyle = {
      position: 'relative', // absolute에서 relative로 변경
      width: '100%', // 항상 100% 너비 보장
      height: useAutoHeight ? 'auto' : `${componentSize.height}px`,
      minHeight: useAutoHeight ? '200px' : undefined, // 자동 높이일 경우 최소 높이 지정
      cursor: 'default',
      opacity: isDragging ? 0.5 : 1,
      border: component.isSelected ? '2px solid #007bff' : 'none',
      boxSizing: 'border-box',
      zIndex: component.isSelected ? 10 : 1,
      paddingLeft: '0px',
      paddingRight: '0px',
      paddingTop: component.style?.paddingTop || '0px',
      paddingBottom: component.style?.paddingBottom || '0px',
      ...(component.style || {})
    };
  } else {
    // 최상위 컴포넌트
    componentStyle = {
      position: 'relative', // absolute에서 relative로 변경
      width: '100%', // 항상 100% 너비 보장
      height: useAutoHeight ? 'auto' : `${componentSize.height}px`,
      minHeight: useAutoHeight ? '200px' : undefined, // 자동 높이일 경우 최소 높이 지정
      opacity: isDragging ? 0.5 : 1,
      border: component.isSelected ? '2px solid #007bff' : 'none',
      boxSizing: 'border-box',
      paddingLeft: '0px',
      paddingRight: '0px',
      paddingTop: component.style?.paddingTop || '0px',
      paddingBottom: component.style?.paddingBottom || '0px',
      transition: 'padding 0.2s ease',
      ...(component.style || {})
    };
  }
  
  // 컴포넌트 클래스 이름에 디바이스 모드 추가
  const componentClassName = `placed-component ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${viewMode === 'mobile' ? 'mobile-view' : viewMode === 'tablet' ? 'tablet-view' : 'desktop-view'}`;
  
  
  return (
    <>
      <TempOne {...component} />
      <TempTwo {...component} />
      <DeviceViewContext.Provider value={viewMode}>
        <div
          id={`component-${component.id}`}
          ref={(node) => {
            drag(drop(node));
            componentRef.current = node;
          }}
          onClick={handleSelect}
          className={componentClassName}
          style={componentStyle}
          data-device={viewMode}
        >
          {renderComponent(component.type, {
            ...component,
            components: allComponents,
            viewMode: viewMode,
            responsiveMode: true,
            isMobile: viewMode === 'mobile',
            isTablet: viewMode === 'tablet',
            isDesktop: viewMode === 'pc',
            deviceType: viewMode
          })}
          
          {isSelected && !(_isChildOfColumn || _isChildOfRow) && (
            <>
              {/* 리사이즈 핸들 */}
              <div className="resize-handle n" onMouseDown={(e) => handleResizeStart(e, 'n')} style={{ top: '-5px', left: '50%', transform: 'translateX(-50%)' }} />
              <div className="resize-handle e" onMouseDown={(e) => handleResizeStart(e, 'e')} style={{ top: '50%', right: '-5px', transform: 'translateY(-50%)' }} />
              <div className="resize-handle s" onMouseDown={(e) => handleResizeStart(e, 's')} style={{ bottom: '-5px', left: '50%', transform: 'translateX(-50%)' }} />
              <div className="resize-handle w" onMouseDown={(e) => handleResizeStart(e, 'w')} style={{ top: '50%', left: '-5px', transform: 'translateY(-50%)' }} />
              
              <div className="resize-handle ne" onMouseDown={(e) => handleResizeStart(e, 'ne')} style={{ top: '-5px', right: '-5px' }} />
              <div className="resize-handle se" onMouseDown={(e) => handleResizeStart(e, 'se')} style={{ bottom: '-5px', right: '-5px' }} />
              <div className="resize-handle sw" onMouseDown={(e) => handleResizeStart(e, 'sw')} style={{ bottom: '-5px', left: '-5px' }} />
              <div className="resize-handle nw" onMouseDown={(e) => handleResizeStart(e, 'nw')} style={{ top: '-5px', left: '-5px' }} />
            </>
          )}
        </div>
      </DeviceViewContext.Provider>
    </>
  );
}

export default ComponentRenderer;

